// api.js — typed client helpers for the Tavern backend
import { io } from "socket.io-client";

export const SERVER = process.env.REACT_APP_SERVER_URL || "http://localhost:22314";

// ── HTTP helpers ──────────────────────────────────────────────────────────────
async function req(method, path, body) {
  const token = localStorage.getItem("tavern_token");
  const res = await fetch(`${SERVER}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

export const api = {
  get:    (p)    => req("GET",    p),
  post:   (p, b) => req("POST",   p, b),
  put:    (p, b) => req("PUT",    p, b),
  patch:  (p, b) => req("PATCH",  p, b),
  delete: (p)    => req("DELETE", p),
};

// ── Auth ──────────────────────────────────────────────────────────────────────
export const auth = {
  register:     (b)    => api.post("/auth/register", b),
  login:        (b)    => api.post("/auth/login", b),
  guest:        (display_name) => api.post("/auth/guest", { display_name }),
  me:           ()     => api.get("/auth/me"),
  mfaSetup:     ()     => api.post("/auth/mfa/setup"),
  mfaVerify:    (code) => api.post("/auth/mfa/verify", { code }),
  mfaChallenge: (b)    => api.post("/auth/mfa/challenge", b),
  mfaDisable:   ()     => api.post("/auth/mfa/disable"),
  phoneSend:    (phone)       => api.post("/auth/phone/send", { phone }),
  phoneVerify:  (phone, code) => api.post("/auth/phone/verify", { phone, code }),
};

// ── Tags (admin-only) ─────────────────────────────────────────────────────────
export const tags = {
  list:         ()              => api.get("/api/tags"),
  allUsers:     ()              => api.get("/api/tags/all-users"),
  allServers:   ()              => api.get("/api/tags/all-servers"),
  create:       (name, color)   => api.post("/api/tags", { name, color }),
  update:       (id, data)      => api.patch(`/api/tags/${id}`, data),
  delete:       (id)            => api.delete(`/api/tags/${id}`),
  setUsers:     (id, user_ids)  => api.put(`/api/tags/${id}/users`, { user_ids }),
  setServers:   (id, server_ids) => api.put(`/api/tags/${id}/servers`, { server_ids }),
  myVisibleUsers: ()            => api.get("/api/tags/my-visible-users"),
};

// ── Temp Rooms ────────────────────────────────────────────────────────────────
export const tempRooms = {
  create:   (name)   => api.post("/api/temp-rooms", { name }),
  mine:     ()       => api.get("/api/temp-rooms/mine"),
  lookup:   (code)   => api.get(`/api/temp-rooms/join/${code}`),
  end:      (id)     => api.post(`/api/temp-rooms/${id}/end`, {}),
  messages: (id)     => api.get(`/api/temp-rooms/${id}/messages`),
};

// ── Pushover ──────────────────────────────────────────────────────────────────
export const pushover = {
  get:    ()     => api.get("/api/pushover"),
  save:   (data) => api.patch("/api/pushover", data),
  test:   ()     => api.post("/api/pushover/test", {}),
};

// ── Servers/channels/messages ─────────────────────────────────────────────────
export const tavern = {
  getServers:       ()                => api.get("/api/servers"),
  getVisibleServers:()                => api.get("/api/servers/visible"),
  createServer:   (b)               => api.post("/api/servers", b),
  joinServer:     (invite)          => api.post("/api/servers/join", { invite }),
  updateServer:   (sid, data)       => api.patch(`/api/servers/${sid}`, data),
  deleteServer:   (sid)             => api.delete(`/api/servers/${sid}`),
  getChannels:    (sid)             => api.get(`/api/servers/${sid}/channels`),
  createChannel:  (sid, name, type) => api.post(`/api/servers/${sid}/channels`, { name, type }),
  deleteChannel:  (cid)             => api.delete(`/api/channels/${cid}`),
  getMembers:     (sid)             => api.get(`/api/servers/${sid}/members`),
  kickMember:     (sid, uid)        => api.delete(`/api/servers/${sid}/members/${uid}`),
  banMember:      (sid, uid)        => api.post(`/api/servers/${sid}/bans/${uid}`, {}),
  getBans:        (sid)             => api.get(`/api/servers/${sid}/bans`),
  unban:          (sid, uid)        => api.delete(`/api/servers/${sid}/bans/${uid}`),
  getMessages:    (cid, opts)       => api.get(`/api/channels/${cid}/messages?limit=${opts?.limit || 50}` + (opts?.before ? `&before=${opts.before}` : "")),
  toggleReaction: (mid, emoji)      => api.post(`/api/messages/${mid}/reactions`, { emoji }),
  getTurnCreds:   ()                => api.get("/turn-credentials"),
  adminGetUsers:  ()                => api.get('/api/admin/users'),
  adminDeleteUser:(uid)             => api.delete(`/api/admin/users/${uid}`),
  // Generic fetch wrappers for AdminPanel (returns raw Response for flexibility)
  get:    (path)       => req("GET",    `/api${path}`),
  post:   (path, body) => req("POST",   `/api${path}`, body),
  put:    (path, body) => req("PUT",    `/api${path}`, body),
  delete: (path)       => req("DELETE", `/api${path}`),
};

// ── Socket singleton ──────────────────────────────────────────────────────────
let socket = null;

export function getSocket(token) {
  if (socket?.connected) return socket;
  if (socket) socket.disconnect();
  socket = io(SERVER, {
    auth: { token },
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionDelay: 1000,
  });
  return socket;
}

export function disconnectSocket() {
  socket?.disconnect();
  socket = null;
}
